import { View } from 'backbone';
import { Consola } from 'consola';
import _ from 'underscore';
import InfoButtonHtml from '../html/infoButton.html';
import InfoPopup from '../html/infoPopup.html';
import { INFO_SECTIONS } from './infoButton.types';
import { getLogger } from './logging/logger';
import InputManager from './manager/inputManager';
import PopUpManager, { EVENTS as POPUP_EVENTS } from './manager/popUpManager';
import fetchData from './util/fetch';
import { addReadyPromise } from './util/utilities';

/**
 * @typedef {import('./util/utilities').IReadyPromise} IReadyPromise
 */

/**
 * @class
 * @type {import('./infoButton.types').InfoPopupOptions & View}
 */
class InfoButton extends View {
	get CN() {
		return InfoButton.name;
	}

	/** @type {Consola} */
	get logger() {
		return getLogger(this.CN);
	}

	className() {
		return 'info-button';
	}

	get template() {
		return _.template(InfoButtonHtml);
	}

	events() {
		return {
			'click .btn-info': this.showInfoPopup,
		};
	}

	constructor(options = {}) {
		super(options);

		this.logger.log('[constructor]', { options });

		const { isReady, onReadyReject, onReadyResolve } = addReadyPromise();

		/** @type {Promise<InfoButton>} */
		this.isReady = isReady;
		/** @type {Function} */
		this.onReadyResolve = onReadyResolve;
		/** @type {Function} */
		this.onReadyReject = onReadyReject;
	}

	initialize(options = {}) {
		this.logger.log(`[${this.initialize.name}]`, { options });

		// Cause singleton
		if (!options) {
			this.logger.debug("Can't do much without options");
			return Promise.resolve();
		}

		this.infoButtonText = 'i';
		this.contentUrl = '';
		this.header = {
			logo: 'img/eeLogo.png',
			title: 'SUPPORT',
			subtitle: 'support+hwptv@hollywoodplayer.com',
		};
		this.content = {
			prefix: '',
			contact: `Copyright © ${new Date().getFullYear()} | Exponential Entertainment, Inc. | All rights reserved.`,
		};
		this.btns = [
			{
				id: 'privacyPolicy',
				text: ['Privacy Policy'],
				uri: 'popup://privacyPolicy',
				className: 'btn btn-popup',
			},
			{
				id: 'termsOfService',
				text: ['Terms of Service'],
				uri: 'popup://termsOfService',
				className: 'btn btn-popup',
			},
			{
				id: 'infoBtnClose',
				text: ['Close'],
				className: 'btn btn-close',
			},
		];
		this.isEnabled = false;

		Object.assign(this, options);

		InputManager.add(INFO_SECTIONS.infoButton, {
			selector: `.${this.className()} .btn-info`,
		});

		return fetchData(this.contentUrl).then(
			(data) => {
				Object.assign(this, data);
				this.onReadyResolve(this);
			},
			(error) => {
				this.logger.warn(`${InfoButton.name} failed to fetch content`, error);
				this.onReadyReject(error);
			},
		);
	}

	render() {
		this.logger.log(`[${this.render.name}]`);

		this.$el.empty().append(this.template({ text: this.infoButtonText }));

		return this;
	}

	disable() {
		this.logger.log(`[${this.disable.name}]`);
		this.isEnabled = false;
		this.$el.hide();
		this.$el.empty();
	}

	enable() {
		this.logger.log(`[${this.enable.name}]`, this);

		this.isReady.finally(() => {
			this.render();
			this.isEnabled = true;
			this.$el.show();
		});
	}

	showInfoPopup() {
		this.logger.log(`[${this.showInfoPopup.name}]`);

		const { header, content, btns = [] } = this;
		this.logger.debug({ header, content, btns });

		PopUpManager.displayDialog({
			id: 'info-popup',
			className: 'info-popup',
			template: InfoPopup,
			btns,
			events: {
				'click .btn-popup': () => {
					PopUpManager.dismissDialog();
					// Wait for Terms, Privacy, or others to be displayed
					PopUpManager.once(POPUP_EVENTS.dialogOpened, () => {
						// Requeue the info pop up so the user returns after reading the Terms or Privacy
						this.showInfoPopup();
					});
				},
				'click .btn-close': () => {
					PopUpManager.dismissDialog();
				},
			},
			...header,
			...content,
		});
	}
}

export default new InfoButton();
